import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ShoppingCart, Plus, Minus, X, Repeat, Package } from "lucide-react";

const GOALS = [
  { id: "recovery", label: "RECOVERY" },
  { id: "performance", label: "PERFORMANCE" },
  { id: "sleep", label: "SLEEP" },
  { id: "all_round", label: "ALL-ROUND" },
];

const CATEGORY_ORDER = ["recovery", "sleep", "performance", "foundation", "bundle"];
const CATEGORY_LABEL = {
  recovery: "RECOVERY",
  sleep: "SLEEP",
  performance: "PERFORMANCE",
  foundation: "FOUNDATION",
  bundle: "BUNDLES",
};

function cartKey(pid, variant, sub) { return `${pid}::${variant}::${sub ? "sub" : "once"}`; }

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [bundle, setBundle] = useState([]);
  const [goal, setGoal] = useState("recovery");
  const [rec, setRec] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data));
    api.get("/supplements/recommendation/today").then((r) => setRec(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.post("/shop/bundle", { goal }).then((r) => setBundle(r.data || [])).catch(() => {});
  }, [goal]);

  useEffect(() => {
    const status = params.get("status");
    const sid = params.get("session_id");
    if (status === "success" && sid) {
      setPollingSession(sid);
      const start = Date.now();
      const iv = setInterval(async () => {
        try {
          const r = await api.get(`/shop/order/status/${sid}`);
          if (r.data.status === "paid") {
            clearInterval(iv);
            setPollingSession(null);
            toast.success("Order confirmed. Welcome to your stack.");
            setParams({});
          } else if (Date.now() - start > 20000) {
            clearInterval(iv);
            setPollingSession(null);
            setParams({});
          }
        } catch {}
      }, 2000);
      return () => clearInterval(iv);
    } else if (status === "cancel") {
      toast("Checkout cancelled");
      setParams({});
    }
  }, [params, setParams]);

  const setLine = (pid, variant, sub, qty) => setCart((c) => {
    const key = cartKey(pid, variant, sub);
    const next = { ...c };
    if (qty <= 0) delete next[key];
    else next[key] = { product_id: pid, variant, subscription: sub, quantity: qty };
    return next;
  });
  const add = (pid, variant = "30day", sub = false) => {
    const key = cartKey(pid, variant, sub);
    const q = (cart[key]?.quantity || 0) + 1;
    setLine(pid, variant, sub, q);
  };

  const price = (p, variant, sub) => {
    const base = p.price * (p.variants[variant]?.multiplier || 1);
    return sub ? base * (1 - p.subscription_discount) : base;
  };

  const cartItems = Object.values(cart).map((it) => ({
    ...it,
    product: products.find((p) => p.id === it.product_id),
  })).filter((i) => i.product);
  const total = cartItems.reduce((a, b) => a + price(b.product, b.variant, b.subscription) * b.quantity, 0);
  const hasSub = cartItems.some((i) => i.subscription);

  const checkout = async () => {
    try {
      const r = await api.post("/shop/checkout", {
        items: Object.values(cart),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 space-y-10" data-testid="shop-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">STACK · SHOP</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-2 leading-none">Stack by signal.</h1>
          <p className="text-neutral-600 mt-3 max-w-xl">Each product paired to a metric. Buy once or subscribe & save 15%.</p>
        </div>
        <Link to="/app/orders" className="btn-outline-ink" data-testid="shop-orders-link">
          <Package className="w-4 h-4" /> My Orders
        </Link>
      </div>

      {pollingSession && (
        <div className="panel p-5 rounded-2xl bg-lime/30 border-lime" data-testid="shop-polling">
          Confirming your payment…
        </div>
      )}

      {rec?.recommendation && rec.product && (
        <div className="panel p-6 rounded-2xl border-l-4 border-l-lime flex items-center justify-between flex-wrap gap-4" data-testid="shop-rec-banner">
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: rec.product.color }} />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">FLYN PICK FOR TODAY</div>
              <div className="font-display text-2xl leading-none">{rec.product.name}</div>
              <div className="text-sm text-neutral-600">{rec.recommendation.reasoning}</div>
            </div>
          </div>
          <button onClick={() => add(rec.product.id)} className="btn-lime" data-testid={`add-recommended-${rec.product.id}`}>
            Add to cart
          </button>
        </div>
      )}

      {/* Category filter */}
      <div className="flex flex-wrap gap-2" data-testid="category-filter">
        {["all", ...CATEGORY_ORDER].map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            data-testid={`cat-${cat}`}
            className={`px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] border transition-all ${categoryFilter === cat ? "bg-obsidian text-white border-obsidian" : "border-neutral-300 hover:border-black"}`}
          >
            {cat === "all" ? "ALL" : CATEGORY_LABEL[cat]}
          </button>
        ))}
      </div>

      {/* Products, grouped by category (or filtered flat) */}
      {CATEGORY_ORDER.filter((c) => categoryFilter === "all" || categoryFilter === c).map((cat) => {
        const items = products.filter((p) => (p.category || "foundation") === cat);
        if (items.length === 0) return null;
        return (
          <div key={cat} data-testid={`category-section-${cat}`}>
            <div className="flex items-baseline justify-between mb-4">
              <div className="eyebrow">{CATEGORY_LABEL[cat]}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                {items.length} PRODUCT{items.length === 1 ? "" : "S"}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {items.map((p) => <ProductCard key={p.id} product={p} onAdd={add} price={price} />)}
            </div>
          </div>
        );
      })}

      {/* Build-your-stack */}
      <div className="panel p-7 rounded-2xl" data-testid="build-stack">
        <div className="eyebrow mb-3">BUILD YOUR STACK · BY GOAL</div>
        <div className="flex flex-wrap gap-2 mb-6">
          {GOALS.map((g) => (
            <button
              key={g.id}
              onClick={() => setGoal(g.id)}
              data-testid={`goal-${g.id}`}
              className={`px-5 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] border transition-all ${goal === g.id ? "bg-obsidian text-white border-obsidian" : "border-neutral-300 hover:border-black"}`}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          {bundle.map((p) => (
            <div key={p.id} className="border border-neutral-200 rounded-xl p-4 flex flex-col">
              <div className="w-3 h-3 rounded-full mb-2" style={{ backgroundColor: p.color }} />
              <div className="font-display text-2xl leading-none">{p.name}</div>
              <div className="text-xs text-neutral-500 mt-1">${p.price.toFixed(0)}</div>
              <button onClick={() => add(p.id)} className="mt-3 text-xs font-mono uppercase tracking-widest text-neutral-700 hover:text-black">+ ADD</button>
            </div>
          ))}
        </div>
      </div>

      {cartItems.length > 0 && (
        <div className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-96 z-20 panel rounded-2xl p-5 shadow-2xl border border-neutral-200" data-testid="cart">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow flex items-center gap-2"><ShoppingCart className="w-3 h-3" /> CART · {cartItems.length}</div>
            <button onClick={() => setCart({})} data-testid="cart-clear"><X className="w-4 h-4 text-neutral-500" /></button>
          </div>
          <div className="space-y-2 mb-4 max-h-52 overflow-y-auto">
            {cartItems.map((i) => (
              <div key={cartKey(i.product_id, i.variant, i.subscription)} className="flex items-center justify-between text-sm">
                <div className="flex-1">
                  <div>{i.product.name} × {i.quantity}</div>
                  <div className="text-[10px] font-mono text-neutral-500 uppercase tracking-widest">
                    {i.product.variants[i.variant]?.label}
                    {i.subscription && <> · <span className="text-lime-dark">MONTHLY -15%</span></>}
                  </div>
                </div>
                <div className="font-mono text-xs">${(price(i.product, i.variant, i.subscription) * i.quantity).toFixed(0)}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-neutral-200 pt-3 mb-4">
            <div className="font-mono text-xs uppercase tracking-widest">
              Total {hasSub && <span className="text-lime-dark">· sub</span>}
            </div>
            <div className="font-display text-2xl">${total.toFixed(0)}</div>
          </div>
          <button onClick={checkout} className="btn-lime w-full" data-testid="checkout-btn">Checkout</button>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onAdd, price }) {
  const p = product;
  const isBundle = (p.category || "") === "bundle";
  const [variant, setVariant] = useState("30day");
  const [sub, setSub] = useState(false);
  return (
    <div className="panel p-6 rounded-2xl flex flex-col" data-testid={`product-card-${p.id}`}>
      <div className="aspect-square rounded-xl mb-4 flex items-center justify-center font-display text-7xl relative" style={{ backgroundColor: p.color, color: p.id === "baseline" ? "#fff" : "#0A0A0A" }}>
        {isBundle ? (
          <span className="font-display text-3xl">{p.name}</span>
        ) : (
          p.name[0]
        )}
        {p.tier === "plus" && (
          <span className="absolute top-2 right-2 bg-obsidian text-lime font-mono text-[9px] tracking-widest px-2 py-1 rounded-full">PLUS</span>
        )}
        {isBundle && (
          <span className="absolute top-2 right-2 bg-white text-obsidian font-mono text-[9px] tracking-widest px-2 py-1 rounded-full">BUNDLE</span>
        )}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{p.tag}</div>
      <div className="font-display text-3xl mt-1 leading-none">{p.name}</div>
      <div className="text-sm text-neutral-600 mt-2 flex-1">{p.subtitle}</div>

      {!isBundle && (
        <div className="mt-4 space-y-2">
          <div className="flex gap-1" data-testid={`variants-${p.id}`}>
            {Object.entries(p.variants || {}).map(([v, meta]) => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                data-testid={`variant-${p.id}-${v}`}
                className={`flex-1 rounded-lg border px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-all ${variant === v ? "border-obsidian bg-obsidian text-white" : "border-neutral-300 hover:border-black"}`}
              >
                {v === "30day" ? "30-DAY" : "90-DAY"}
              </button>
            ))}
          </div>
          <button
            onClick={() => setSub((s) => !s)}
            data-testid={`sub-toggle-${p.id}`}
            className={`w-full rounded-lg border px-2 py-2 font-mono text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${sub ? "border-lime bg-lime text-obsidian" : "border-neutral-300 hover:border-black"}`}
          >
            <Repeat className="w-3 h-3" /> {sub ? "SUBSCRIBING · 15% OFF" : "SUBSCRIBE · SAVE 15%"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
        <div className="font-mono text-sm">${(isBundle ? p.price : price(p, variant, sub)).toFixed(0)}</div>
        <button onClick={() => onAdd(p.id, variant, isBundle ? false : sub)} data-testid={`add-cart-${p.id}`} className="btn-lime !py-2 !px-4 !text-[10px]">
          Add
        </button>
      </div>
    </div>
  );
}
